import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../utils/toast";
import {
  createCardView,
  createContact,
  deleteOpportunityCard,
  deleteReadyRosterCard,
  fetchCountries,
  fetchCurrencyOptions,
  fetchOpportunityTypes,
  fetchPurseOptions,
  fetchRosterStatus,
  fetchTrainingDisciplines, 
  fetchWeightClasses,
  getAllMarketplaceCards,
  getAllTemplates,
  getConnectStatus,
  sendMessage,
  updateReadyRosterCardStatus
} from "../../services/api";
import { CardViewRequestDto, MarketplaceCardResponseDto, OpportunityCardTemplate } from "../../types/types";

export const useMarketplaceCards = () => {
  const { t, i18n } = useTranslation("marketPlaceCards");
  const tRef = useRef(t);
  const loginResponse = JSON.parse(localStorage.getItem("loginResponse") || "{}");
  const profileType = loginResponse?.profileType;
  const username = loginResponse?.username;
  const managerId = loginResponse?.id;

  const [activeTab, setRawActiveTab] = useState<"all" | "myCards">("all");
  const [currentMonth, setCurrentMonth] = useState<CalendarDate>(today(getLocalTimeZone()));
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [confirmContactPopup, setConfirmContactPopup] = useState(false);
  const [marketplaceId, setMarketplaceId] = useState<number | null>(null);
  const [rosterStatus, setRosterStatus] = useState<string[]>([]);
  const [selectedRosterStatus, setRawSelectedRosterStatus] = useState("");
  const [selectedTitle, setRawSelectedTitle] = useState("");
  const [searchName, setRawSearchName] = useState("");
  const [filterCardType, setRawFilterCardType] = useState("");
  const [filterLocation, setRawFilterLocation] = useState("");
  const [filterSport, setRawFilterSport] = useState("");
  const [filterWeightClass, setRawFilterWeightClass] = useState("");
  const [filterPurse, setRawFilterPurse] = useState("");
  const [filterCurrency, setRawFilterCurrency] = useState("");
  const [filterOpportunityType, setRawFilterOpportunityType] = useState("");
  const [cards, setCards] = useState<MarketplaceCardResponseDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [showReportPopup, setShowReportPopup] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const pageRef = useRef(0);
  const [debouncedSearchName, setDebouncedSearchName] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 12;
  const [isCreateRosterModalOpen, setIsCreateRosterModalOpen] = useState(false);
  const [isOpportunityModalOpen, setIsOpportunityModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<MarketplaceCardResponseDto | null>(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState<any>(null);
  const [viewCardId, setViewCardId] = useState<string | number | null>(null);
  const [isOpportunityViewOpen, setIsOpportunityViewOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [confirmPublishPopup, setConfirmPublishPopup] = useState(false);
  const [locations, setLocations] = useState<string[]>([]);
  const [sports, setSports] = useState<string[]>([]);
  const [weightClasses, setWeightClasses] = useState<string[]>([]);
  const [purses, setPurses] = useState<string[]>([]);
  const [opportunityTypes, setOpportunityTypes] = useState<string[]>([]);
  const [connectStatus, setConnectStatus] = useState<string | null>(null);
  const [connectStatusLoading, setConnectStatusLoading] = useState(false);

  const [templates, setTemplates] = useState<OpportunityCardTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // Any filter/tab change must go back to page 1, otherwise page 3 + a filter shows an empty list.
  const withPageReset =
    <T>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      pageRef.current = 0;
      setCurrentPage(1);
    };
  const setActiveTab = withPageReset(setRawActiveTab);
  const setSearchName = setRawSearchName; // page is reset when the debounced value applies
  const setFilterCardType = withPageReset(setRawFilterCardType);
  const setFilterLocation = withPageReset(setRawFilterLocation);
  const setFilterSport = withPageReset(setRawFilterSport);
  const setFilterWeightClass = withPageReset(setRawFilterWeightClass);
  const setFilterPurse = withPageReset(setRawFilterPurse);
  const setFilterCurrency = withPageReset(setRawFilterCurrency);
  const setFilterOpportunityType = withPageReset(setRawFilterOpportunityType);
  const setSelectedRosterStatus = withPageReset(setRawSelectedRosterStatus);
  const setSelectedTitle = withPageReset(setRawSelectedTitle);

  useEffect(() => {
    const timer = setTimeout(() => {
      pageRef.current = 0;
      setDebouncedSearchName(searchName);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchName]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const loadConnectStatus = useCallback(async () => {
    if (activeTab !== "myCards" || !username) {
      setConnectStatus(null);
      return;
    }

    setConnectStatusLoading(true);
    try {
      const status = await getConnectStatus();
      setConnectStatus(status.trim().toUpperCase());
    } catch (err) {
      setConnectStatus(null);
    } finally {
      setConnectStatusLoading(false);
    }
  }, [activeTab, username]);

  useEffect(() => {
    loadConnectStatus();
  }, [loadConnectStatus]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem("i18nextLng") || "en";
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const countries = await fetchCountries();
        setLocations(countries);
      } catch (e) {
        toast.error(tRef.current("failed_to_load_initial_data"));
      }
    };
    loadInitialData();
  }, []);

  const loadPursesIfNeeded = async () => {
    if (purses.length === 0) {
      try {
        const data = await fetchPurseOptions();
        setPurses(data);
      } catch (error) {
        toast.error(t("failed_to_load_purse_options"));
      }
    }
  };

  const handleCreateOpportunity = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const data = await getAllTemplates();
      setTemplates(data || []);
    } catch (err) {
      toast.error(tRef.current("failed_to_load_templates"));
      setTemplates([]);
      throw err;
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  const loadSportsIfNeeded = async () => {
    if (sports.length === 0) {
      try {
        const data = await fetchTrainingDisciplines();
        setSports(data);
      } catch (error) {
        toast.error(t("failed_to_load_training_disciplines"));
      }
    }
  };

  const loadCurrenciesIfNeeded = async () => {
    if (currencies.length === 0) {
      try {
        const data = await fetchCurrencyOptions();
        setCurrencies(data);
      } catch (error) {
        toast.error(t("failed_to_load_currency_options"));
      }
    }
  };

  const loadWeightClassesIfNeeded = async () => {
    if (weightClasses.length === 0) {
      try {
        const data = await fetchWeightClasses();
        setWeightClasses(data);
      } catch (error) {
        toast.error(t("failed_to_load_weight_classes"));
      }
    }
  };

  const loadOpportunityTypeIfNeeded = async () => {
    if (opportunityTypes.length === 0) {
      try {
        const data = await fetchOpportunityTypes();
        setOpportunityTypes(data);
      } catch (error) {
        toast.error(t("failed_to_load_opportunity_types"));
      }
    }
  };

  const loadRosterStatusesIfNeeded = useCallback(async () => {
    if (rosterStatus.length === 0) {
      try {
        const rosterStatusData = await fetchRosterStatus();
        setRosterStatus(rosterStatusData);
      } catch (e) {
        toast.error(tRef.current("failed_to_load_roster_statuses"));
      }
    }
  }, [rosterStatus.length]);

  useEffect(() => {
    const loadAllFilters = async () => {
      await Promise.all([
        loadPursesIfNeeded(),
        loadSportsIfNeeded(),
        loadCurrenciesIfNeeded(),
        loadWeightClassesIfNeeded(),
        loadOpportunityTypeIfNeeded(),
        loadRosterStatusesIfNeeded(),
      ]);
    };
    loadAllFilters();
  }, []);

  const latestRequestId = useRef(0);

  const buildFilters = useCallback(
    (pageNum: number): any => ({
      name: debouncedSearchName || undefined,
      role: filterCardType || undefined,
      location: filterLocation || undefined,
      sport: filterSport || undefined,
      weightClass: filterWeightClass || undefined,
      purse: filterPurse || undefined,
      currency: filterCurrency || undefined,
      opportunityType: filterOpportunityType || undefined,
      rosterStatus: selectedRosterStatus || undefined,
      titleShot: selectedTitle === "All Titles" ? undefined : selectedTitle,
      myCard: activeTab === "myCards" ? true : undefined,
      page: pageNum,
      size: pageSize,
    }),
    [
      debouncedSearchName,
      filterCardType,
      filterLocation,
      filterSport,
      filterWeightClass,
      filterPurse,
      filterCurrency,
      filterOpportunityType,
      selectedRosterStatus,
      selectedTitle,
      activeTab,
    ]
  );

  const fetchMarketplaceCards = useCallback(async () => {
    const requestId = ++latestRequestId.current;
    pageRef.current = 0;
    setCurrentPage(1);
    setLoading(true);
    try {
      const filters = buildFilters(0);
      const data = await getAllMarketplaceCards(username || "", filters);
      if (requestId !== latestRequestId.current) return;

      if (data && data.content) {
        setCards(data.content);
        setTotalPages(data.totalPages || 1);
        const hasNext = !data.last && ((data.number ?? 0) + 1 < (data.totalPages ?? 1));
        setHasMore(hasNext);
      } else {
        setCards([]);
        setTotalPages(1);
        setHasMore(false);
      }
    } catch (e) {
      if (requestId !== latestRequestId.current) return;
      toast.error(tRef.current("failed_to_load_cards"));
      setCards([]);
      setTotalPages(1);
      setHasMore(false);
    } finally {
      if (requestId === latestRequestId.current) setLoading(false);
    }
  }, [buildFilters, username]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    const nextPage = pageRef.current + 1;
    const requestId = latestRequestId.current;
    setLoadingMore(true);
    try {
      const filters = buildFilters(nextPage);
      const data = await getAllMarketplaceCards(username || "", filters);
      if (requestId !== latestRequestId.current) return;

      if (data && data.content && data.content.length > 0) {
        pageRef.current = nextPage;
        setCurrentPage(nextPage + 1);
        setCards((prev) => {
          const existingIds = new Set(prev.map((c) => c.id));
          const newItems = data.content.filter((c) => !existingIds.has(c.id));
          return [...prev, ...newItems];
        });
        setTotalPages(data.totalPages || 1);
        const hasNext = !data.last && ((data.number ?? nextPage) + 1 < (data.totalPages ?? 1));
        setHasMore(hasNext);
      } else {
        setHasMore(false);
      }
    } catch (e) {
      if (requestId !== latestRequestId.current) return;
      toast.error(tRef.current("failed_to_load_cards"));
      setHasMore(false);
    } finally {
      if (requestId === latestRequestId.current) setLoadingMore(false);
    }
  }, [loading, loadingMore, hasMore, buildFilters, username]);

  useEffect(() => {
    fetchMarketplaceCards();
  }, [fetchMarketplaceCards]);

  const trackGeoLocation = async (viewedCardId?: string | number) => {
    try {
      if (!viewedCardId) return;
      const storageKey = `lastViewed_${viewedCardId}`;
      const lastViewed = localStorage.getItem(storageKey);
      if (lastViewed && Date.now() - Number(lastViewed) < 5 * 60 * 1000) return;

      const geoRes = await fetch("https://ipapi.co/json");
      const geoData = await geoRes.json();

      const userJson = localStorage.getItem("loginResponse");
      const user = userJson ? JSON.parse(userJson) : null;
      if (!user?.username) return;

      const dto: CardViewRequestDto = {
        marketplaceCardId: viewedCardId,
        informationRequestDto: {
          ipAddress: geoData.ip,
          countryCode: geoData.country_code,
          city: geoData.city,
          browserInfo: navigator.userAgent,
          sourcePage: window.location.pathname,
        },
      };
      await createCardView(user.username, dto);
      localStorage.setItem(storageKey, Date.now().toString());
    } catch (error: any) {}
  };

  const handleResetFilters = useCallback(() => {
    setRawSearchName("");
    setDebouncedSearchName("");
    setRawFilterCardType("");
    setRawFilterLocation("");
    setRawFilterSport("");
    setRawFilterWeightClass("");
    setRawFilterPurse("");
    setRawFilterCurrency("");
    setRawFilterOpportunityType("");
    setRawSelectedRosterStatus("");
    setRawSelectedTitle("");
    pageRef.current = 0;
    setCurrentPage(1);
  }, []);

  const handleViewModalClose = useCallback(() => {
    setIsViewModalOpen(false);
    setSelectedCard(null);
    setConfirmPublishPopup(false);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedCard(null);
  }, []);

  const handlePublish = useCallback(
    async (cardId: string) => {
      if (isPublishing) return;

      setIsPublishing(true);
      try {
        const parsedCardId = parseInt(cardId, 10);
        if (isNaN(parsedCardId)) throw new Error("Invalid card ID");

        await updateReadyRosterCardStatus(parsedCardId);
        setIsViewModalOpen(false);
        fetchMarketplaceCards();
        toast.success(t("card_published_successfully"));
      } catch (error) {
        toast.error(t("failed_to_publish_card"));
      } finally {
        setIsPublishing(false);
      }
    },
    [fetchMarketplaceCards, isPublishing, t]
  );

  const handleEdit = useCallback(
    async (payload: { type: string; data: any }) => {
      try {
        if (payload.type === "READYROSTER") {
          setSelectedCard(payload.data);
          setIsEditModalOpen(true);
        } else if (payload.type === "OPPORTUNITY") {
          const opportunityStatus =
            payload.data?.opportunityCardResponse?.status?.toUpperCase();

          if (opportunityStatus !== "AVAILABLE") {
            toast.error(
              t("only_available_opportunity_cards_can_be_edited", {
                defaultValue: "Only available opportunity cards can be edited",
              }),
            );
            return;
          }

          setSelectedOpportunity(payload.data);
          try {
            await handleCreateOpportunity();
          } catch {
          }
          setIsOpportunityModalOpen(true);
        }
      } catch (error) {
        toast.error(t("failed_to_open_edit_modal"));
      }
    },
    [handleCreateOpportunity, t]
  );

  const handleDelete = useCallback(
    async (card: MarketplaceCardResponseDto) => {
      try {
        const cardId = card.id.toString();
        if (card.readyRosterCard) {
          await deleteReadyRosterCard(parseInt(cardId, 10));
        } else if (card.opportunityCardResponse) {
          const opportunityStatus =
            card.opportunityCardResponse.status?.toUpperCase();

          if (opportunityStatus !== "AVAILABLE") {
            toast.error(
              t("only_available_opportunity_cards_can_be_deleted", {
                defaultValue: "Only available opportunity cards can be deleted",
              }),
            );
            return;
          }

          await deleteOpportunityCard(cardId);
        } else {
          throw new Error("Unknown card type");
        }
        fetchMarketplaceCards();
        toast.success(t("card_deleted_successfully"));
      } catch (error) {
        toast.error(t("failed_to_delete_card"));
      }
    },
    [fetchMarketplaceCards, t]
  );

  const handleView = useCallback(
    (card: MarketplaceCardResponseDto) => {
      try {
        setSelectedCard(card);
        setIsViewModalOpen(true);
        trackGeoLocation(card.id);
      } catch (error) {
        toast.error(t("failed_to_open_view_modal"));
      }
    },
    [t]
  );

  const handleOpportunityView = useCallback(
    (cardId: string | number) => {
      try {
        setViewCardId(cardId);
        setIsOpportunityViewOpen(true);
      } catch (error) {
        toast.error(t("failed_to_open_opportunity_view"));
      }
    },
    [t]
  );

const handleCreateContact = useCallback(
  async (card: MarketplaceCardResponseDto, e?: any) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (!card) return;

    try {
      const fighter = card.readyRosterCard?.fighter;
      const receiverId = fighter?.id;
      const receiverName = `${fighter?.firstName || card.readyRosterCard?.firstName || ""} ${fighter?.lastName || card.readyRosterCard?.lastName || ""}`.trim();
      const marketplaceCardId = card?.id ?? selectedCard?.id;

      if (!marketplaceCardId) {
        toast.error(t("missing_sender_or_receiver"));
        return;
      }

      const contact = await createContact("readyroster", marketplaceCardId);

      if (contact && receiverId) {
        await sendMessage({
          receiverId,
          message: `Hi ${receiverName}! I would like to contact you regarding your Ready Roster card.`,
          chatType: "INDIVIDUAL",
          type: "CONTENT",
        });
      }

      if (contact) {
        toast.success(t("contact_invite_success"));
        fetchMarketplaceCards();
        setIsViewModalOpen(false);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t("failed_to_create_contact"));
    }
  },
  [t, fetchMarketplaceCards, selectedCard]
);

  return {
    t,
    templates,
    templatesLoading,
    handleCreateOpportunity,
    profileType,
    username,
    managerId,
    loginResponse,
    activeTab,
    setActiveTab,
    currentMonth,
    setCurrentMonth,
    currencies,
    confirmContactPopup,
    setConfirmContactPopup,
    marketplaceId,
    rosterStatus,
    selectedRosterStatus,
    setSelectedRosterStatus,
    selectedTitle,
    setSelectedTitle,
    searchName,
    setSearchName,
    filterCardType,
    setFilterCardType,
    filterLocation,
    setFilterLocation,
    filterSport,
    setFilterSport,
    filterWeightClass,
    setFilterWeightClass,
    filterPurse,
    setFilterPurse,
    filterCurrency,
    setFilterCurrency,
    filterOpportunityType,
    setFilterOpportunityType,
    cards,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    showReportPopup,
    setShowReportPopup,
    currentPage,
    setCurrentPage,
    totalPages,
    isCreateRosterModalOpen,
    setIsCreateRosterModalOpen,
    isOpportunityModalOpen,
    setIsOpportunityModalOpen,
    isViewModalOpen,
    isEditModalOpen,
    selectedCard,
    selectedOpportunity,
    setSelectedOpportunity,
    viewCardId,
    isOpportunityViewOpen,
    setIsOpportunityViewOpen,
    isPublishing,
    confirmPublishPopup,
    setConfirmPublishPopup,
    locations,
    sports,
    weightClasses,
    purses,
    opportunityTypes,
    fetchMarketplaceCards,
    handleResetFilters,
    handleViewModalClose,
    handleEditModalClose,
    handlePublish,
    handleEdit,
    handleDelete,
    handleView,
    handleOpportunityView,
    handleCreateContact,
    loadPursesIfNeeded,
    loadCurrenciesIfNeeded,
    loadWeightClassesIfNeeded,
    loadSportsIfNeeded,
    loadOpportunityTypeIfNeeded,
    connectStatus,
    connectStatusLoading
  };
};
